import React, { useState } from 'react';
import { DB, uploadImage } from '../data/database';
import { ROLES } from '../data/constants';
import { CheckIcon, CameraIcon, CarIcon, EngineIcon, PartsIcon } from './Icons';
import { logActivity, Notifications } from '../data/notifications';
import centralUpdateStatus, { detectUnitType } from '../utils/updateUnitStatus';

const CONDITIONS = [
  { id: 'good',          label: 'Good',          icon: '✅', color: 'var(--green)' },
  { id: 'minor_damage',  label: 'Minor Damage',  icon: '⚠️', color: 'var(--yellow)' },
  { id: 'major_damage',  label: 'Major Damage',  icon: '🔴', color: 'var(--red)' },
  { id: 'missing_parts', label: 'Missing Parts',  icon: '❌', color: 'var(--orange, var(--yellow))' },
];

function ContainerArrival({ container, user, onUpdate, onOpenScanner }) {
  const [activeUnit, setActiveUnit]     = useState(null);
  const [condition, setCondition]       = useState('good');
  const [damagePhotos, setDamagePhotos] = useState([]);
  const [damageNotes, setDamageNotes]   = useState('');
  const [uploading, setUploading]       = useState(false);
  const [showSummary, setShowSummary]   = useState(false);

  const role = ROLES[user.role] || {};
  const isAdmin = user.role === 'head_admin' || user.role === 'admin';
  const isCustomer = role.isCustomer;

  const allUnits = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
  const customers = DB.get('customers') || [];

  // Determine which units to show
  let myUnits;
  if (isCustomer) {
    myUnits = (container.units || []).map(uid => allUnits.find(u => u.id === uid)).filter(u => u && u.assignedTo === user.id);
  } else {
    // Admin/staff see all units
    myUnits = (container.units || []).map(uid => allUnits.find(u => u.id === uid)).filter(Boolean);
  }

  const unitsData = container.unitsData || [];
  const customer = customers.find(c => c.id === user.id);

  // Only show for arrived/delivered containers
  if (!['arrived', 'delivered', 'closed', 'at_port', 'customs'].includes(container.status)) return null;
  if (myUnits.length === 0) return null;

  const getArrivalData = (unitId) => {
    const ud = unitsData.find(d => d.unit_id === unitId);
    return ud?.arrival_data || null;
  };

  const receivedCount = myUnits.filter(u => getArrivalData(u.id)?.received).length;
  const allReceived = myUnits.length > 0 && receivedCount === myUnits.length;

  const getName = (u) => u.make ? `${u.make} ${u.model}` : u.engineName || u.partName || 'Unknown';
  const getIcon = (u) => u.make ? <CarIcon /> : u.engineName ? <EngineIcon /> : <PartsIcon />;

  const uploadPhoto = async (file) => {
    if (!file || !file.type.startsWith('image/')) return '';
    setUploading(true);
    const compressed = await new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const MAX = 1200;
          let { width, height } = img;
          if (width > MAX || height > MAX) {
            if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
            else { width = Math.round(width * MAX / height); height = MAX; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.75));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
    const url = await uploadImage(compressed);
    setDamagePhotos(prev => [...prev, url]);
    setUploading(false);
    return url;
  };

  const save = (updates) => {
    const updated = { ...container, ...updates };
    const containers = DB.get('containers') || [];
    DB.set('containers', containers.map(c => c.id === container.id ? updated : c));
    onUpdate(updated);
  };

  const markSingleUnitDelivered = (unitId, unitCondition = 'good', photos = [], notes = '') => {
    const updatedData = [...(container.unitsData || [])];
    const existing = updatedData.findIndex(ud => ud.unit_id === unitId);
    const arrivalEntry = {
      received: true,
      received_at: new Date().toISOString(),
      received_by: user.id,
      received_by_name: user.name,
      condition: unitCondition,
      damage_photos: photos,
      damage_notes: notes,
    };

    if (existing >= 0) {
      updatedData[existing] = { ...updatedData[existing], arrival_data: arrivalEntry };
    } else {
      updatedData.push({ unit_id: unitId, arrival_data: arrivalEntry });
    }

    save({ unitsData: updatedData });

    // Update unit status via central function
    const unit = allUnits.find(u => u.id === unitId);
    if (unit) {
      centralUpdateStatus(unit.id, detectUnitType(unit), 'delivered', { country: customer?.country || user.country || unit.country, updatedBy: user.name });
    }

    if (unitCondition !== 'good') {
      const unitName = getName(allUnits.find(u => u.id === unitId) || {});
      Notifications.add('container_arrived', 'DAMAGE REPORTED',
        `${user.name} reported ${unitCondition.replace(/_/g, ' ')} on ${unitName} from container ${container.number || container.id}${notes ? ` — "${notes}"` : ''}`, {});
    }
  };

  const markAllArrived = () => {
    if (!window.confirm(`Mark all ${myUnits.length - receivedCount} units as arrived in good condition?`)) return;
    const updatedData = [...(container.unitsData || [])];
    const now = new Date().toISOString();

    myUnits.forEach(unit => {
      if (getArrivalData(unit.id)?.received) return;
      const existing = updatedData.findIndex(ud => ud.unit_id === unit.id);
      const arrivalEntry = {
        received: true, received_at: now, received_by: user.id, received_by_name: user.name,
        condition: 'good', damage_photos: [], damage_notes: '',
      };
      if (existing >= 0) updatedData[existing] = { ...updatedData[existing], arrival_data: arrivalEntry };
      else updatedData.push({ unit_id: unit.id, arrival_data: arrivalEntry });

      centralUpdateStatus(unit.id, detectUnitType(unit), 'delivered', { country: customer?.country || user.country || unit.country, updatedBy: user.name });
    });

    // Check if all customers have confirmed
    const allContainerUnits = (container.units || []).map(uid => allUnits.find(u => u.id === uid)).filter(Boolean);
    const allWillBeReceived = allContainerUnits.every(u => {
      const existing = updatedData.find(d => d.unit_id === u.id);
      return existing?.arrival_data?.received;
    });

    save({
      unitsData: updatedData,
      ...(allWillBeReceived ? { status: 'delivered', allUnitsAccounted: true } : {}),
    });
    logActivity.containerArrived?.(container.number || container.id, user.name);

    Notifications.add('container_arrived', 'All Units Arrived',
      `${user.name} confirmed all units arrived for container ${container.number || ''} — ${myUnits.length} units in good condition`, {});

    setShowSummary(true);
  };

  const markUnitWithCondition = (unitId) => {
    markSingleUnitDelivered(unitId, condition, damagePhotos, damageNotes);
    setActiveUnit(null);
    setCondition('good');
    setDamagePhotos([]);
    setDamageNotes('');
  };

  const quickMarkUnit = (unitId) => {
    markSingleUnitDelivered(unitId, 'good', [], '');
  };

  // ── Summary after all done ──
  if (showSummary || (allReceived && container.allUnitsAccounted)) {
    const goodCount = myUnits.filter(u => getArrivalData(u.id)?.condition === 'good').length;
    const damageCount = myUnits.length - goodCount;

    return (
      <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        <div className="arrival-summary">
          <div style={{ fontSize: 24, marginBottom: 8 }}>✅</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--green)', marginBottom: 8 }}>Arrival Complete</div>
          <div style={{ fontSize: 12, color: 'var(--text2)' }}>
            Good condition: {goodCount} unit{goodCount !== 1 ? 's' : ''}
          </div>
          {damageCount > 0 && (
            <div style={{ fontSize: 12, color: 'var(--yellow)', marginTop: 4 }}>
              Issues reported: {damageCount} unit{damageCount !== 1 ? 's' : ''}
            </div>
          )}
          {damageCount > 0 && (
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>
              Your damage reports have been submitted. Our team will review and follow up.
            </div>
          )}
        </div>

        {/* Show individual results */}
        {myUnits.map(unit => {
          const arrData = getArrivalData(unit.id);
          const cond = CONDITIONS.find(c => c.id === arrData?.condition);
          return (
            <div key={unit.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 12 }}>
              <span style={{ color: cond?.color || 'var(--green)' }}>{cond?.icon || '✅'}</span>
              <span>{getName(unit)}</span>
              <span style={{ color: cond?.color || 'var(--green)', marginLeft: 'auto' }}>{cond?.label || 'Good'}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // ── Admin view: Arrival Reports ──
  if (isAdmin) {
    // Group arrival data by customer
    const arrivalsByCustomer = {};
    myUnits.forEach(unit => {
      const arrData = getArrivalData(unit.id);
      if (!arrData?.received) return;
      const custId = unit.assignedTo;
      const custName = custId ? (customers.find(c => c.id === custId)?.name || 'Unknown') : 'Unassigned';
      if (!arrivalsByCustomer[custName]) arrivalsByCustomer[custName] = { units: [], receivedAt: arrData.received_at, receivedBy: arrData.received_by_name };
      arrivalsByCustomer[custName].units.push({ unit, arrData });
    });

    const hasAnyArrivals = Object.keys(arrivalsByCustomer).length > 0;
    const unreceived = myUnits.filter(u => !getArrivalData(u.id)?.received);

    return (
      <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--blue)', marginBottom: 12 }}>
          Arrival Reports
        </div>

        {hasAnyArrivals && Object.entries(arrivalsByCustomer).map(([custName, data]) => (
          <div key={custName} style={{ marginBottom: 16, padding: 12, background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
              {custName} — Received {data.receivedAt?.split('T')[0]}
            </div>

            {data.units.map(({ unit, arrData }) => {
              const cond = CONDITIONS.find(c => c.id === arrData.condition);
              const hasDamage = arrData.condition !== 'good';
              // Get loading photos from unitsData
              const loadingData = unitsData.find(d => d.unit_id === unit.id);
              const loadingPhotos = loadingData?.loading_photos || [];

              return (
                <div key={unit.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: cond?.color }}>{cond?.icon}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>{getName(unit)}</span>
                    <span style={{ fontSize: 11, color: cond?.color }}>{cond?.label}</span>
                  </div>

                  {hasDamage && (
                    <>
                      {arrData.damage_notes && (
                        <div style={{ fontSize: 11, color: 'var(--yellow)', marginTop: 4, fontStyle: 'italic' }}>
                          "{arrData.damage_notes}"
                        </div>
                      )}

                      {/* Photo comparison: loading vs arrival */}
                      {(loadingPhotos.length > 0 || arrData.damage_photos?.length > 0) && (
                        <div className="arrival-compare" style={{ marginTop: 8 }}>
                          <div className="arrival-compare-col">
                            <div style={{ fontWeight: 600 }}>Loading Photos</div>
                            {loadingPhotos.length > 0 ? (
                              loadingPhotos.slice(0, 2).map((url, i) => <img key={i} src={url} alt="Loading" />)
                            ) : (
                              <div style={{ padding: 20, background: 'var(--bg)', borderRadius: 8, marginTop: 4 }}>No loading photos</div>
                            )}
                          </div>
                          <div className="arrival-compare-col">
                            <div style={{ fontWeight: 600 }}>Arrival Photos</div>
                            {arrData.damage_photos?.length > 0 ? (
                              arrData.damage_photos.slice(0, 2).map((url, i) => <img key={i} src={url} alt="Arrival" />)
                            ) : (
                              <div style={{ padding: 20, background: 'var(--bg)', borderRadius: 8, marginTop: 4 }}>No arrival photos</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Admin actions for damage */}
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                        <button className="btn btn-success btn-sm" style={{ fontSize: 10 }}>Accept Claim</button>
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }}>Dispute</button>
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }}>File Insurance</button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {unreceived.length > 0 && (
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>
            {unreceived.length} unit{unreceived.length !== 1 ? 's' : ''} not yet confirmed by customer
          </div>
        )}

        {!hasAnyArrivals && (
          <div style={{ textAlign: 'center', padding: 16, color: 'var(--text3)', fontSize: 12 }}>
            No arrival reports yet — waiting for customers to confirm
          </div>
        )}
      </div>
    );
  }

  // ── Customer / Destination arrival flow ──
  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
      {/* Banner */}
      <div className="arrival-banner">
        <div className="arrival-banner-title">Your Container Has Arrived</div>

        {/* Quick path: Mark All */}
        {!allReceived && (
          <>
            <button className="btn btn-success" style={{ width: '100%', padding: '16px 20px', fontSize: 15, marginBottom: 8 }} onClick={markAllArrived}>
              <CheckIcon /> Mark All Units as Arrived
            </button>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>
              All {myUnits.length - receivedCount} units will be marked as received in good condition.
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
                For detailed condition reporting, scan each unit's QR code.
              </div>
            </div>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              <span style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>or inspect individually</span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>

            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>
              Want to check each unit? Scan QR codes or tap each unit below.
            </div>

            {onOpenScanner && (
              <button className="btn btn-secondary" style={{ marginBottom: 12 }} onClick={onOpenScanner}>
                📷 Scan QR Code
              </button>
            )}
          </>
        )}
      </div>

      {/* Progress */}
      {receivedCount > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{receivedCount} of {myUnits.length} received</div>
          <div style={{ height: 6, background: 'var(--bg3)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(receivedCount / myUnits.length) * 100}%`, background: allReceived ? 'var(--green)' : 'var(--blue)', transition: 'width 0.3s', borderRadius: 3 }} />
          </div>
        </div>
      )}

      {uploading && <div style={{ fontSize: 12, color: 'var(--blue)', marginBottom: 10 }}>Uploading...</div>}

      {/* Unit list */}
      {myUnits.map(unit => {
        const arrData = getArrivalData(unit.id);
        const isReceived = arrData?.received;
        const isActive = activeUnit === unit.id;

        return (
          <div key={unit.id} className="arrival-unit-row" style={{
            borderColor: isReceived ? 'rgba(63,185,80,0.3)' : isActive ? 'var(--blue)' : 'var(--border)',
            background: isReceived ? 'rgba(63,185,80,0.06)' : 'var(--bg2)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
              <div style={{ width: 40, height: 30, borderRadius: 4, overflow: 'hidden', background: 'var(--bg)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {unit.images?.length
                  ? <img src={unit.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ color: 'var(--text3)' }}>{getIcon(unit)}</span>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getName(unit)}</div>
                <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{unit.sku}</div>
              </div>

              {isReceived ? (
                <div style={{ flexShrink: 0, textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: 'var(--green)', fontWeight: 700 }}>Received</div>
                  <div style={{ fontSize: 10, color: CONDITIONS.find(c => c.id === arrData.condition)?.color || 'var(--green)' }}>
                    {CONDITIONS.find(c => c.id === arrData.condition)?.label || 'Good'}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  <button className="btn btn-success btn-sm" style={{ fontSize: 10, padding: '6px 10px' }}
                    onClick={() => quickMarkUnit(unit.id)} title="Mark as arrived in good condition">
                    <CheckIcon /> Arrived
                  </button>
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '6px 8px' }}
                    onClick={() => { setActiveUnit(isActive ? null : unit.id); setCondition('good'); setDamagePhotos([]); setDamageNotes(''); }}
                    title="Report condition">
                    {isActive ? '×' : '📸'}
                  </button>
                </div>
              )}
            </div>

            {/* Condition report form */}
            {isActive && !isReceived && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)', width: '100%' }}>
                <div style={{ marginBottom: 10 }}>
                  <label className="form-label" style={{ fontSize: 11 }}>How did this unit arrive?</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    {CONDITIONS.map(c => (
                      <button key={c.id} type="button"
                        className={`btn btn-sm ${condition === c.id ? 'btn-primary' : 'btn-secondary'}`}
                        style={{ fontSize: 11, padding: '8px 6px' }}
                        onClick={() => setCondition(c.id)}>
                        {c.icon} {c.label}
                      </button>
                    ))}
                  </div>
                </div>

                {condition !== 'good' && (
                  <>
                    <div style={{ marginBottom: 10 }}>
                      <label className="form-label" style={{ fontSize: 11 }}>Photos {condition !== 'good' ? '(required)' : ''}</label>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                        {damagePhotos.map((url, i) => (
                          <div key={i} style={{ position: 'relative' }}>
                            <img src={url} alt="" style={{ width: 56, height: 42, borderRadius: 4, objectFit: 'cover' }} />
                            <button onClick={() => setDamagePhotos(prev => prev.filter((_, idx) => idx !== i))}
                              style={{ position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: '50%', background: 'var(--red)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer' }}>×</button>
                          </div>
                        ))}
                        <label style={{ width: 56, height: 42, borderRadius: 4, border: '2px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text3)' }}>
                          <CameraIcon />
                          <input type="file" accept="image/*" capture="environment" multiple style={{ display: 'none' }}
                            onChange={async e => { for (const f of e.target.files) await uploadPhoto(f); e.target.value = ''; }} />
                        </label>
                      </div>
                    </div>

                    <div className="form-group" style={{ marginBottom: 10 }}>
                      <label className="form-label" style={{ fontSize: 11 }}>Describe the issue</label>
                      <textarea className="form-input" value={damageNotes} onChange={e => setDamageNotes(e.target.value)}
                        placeholder="Describe the damage or missing parts" rows={2} style={{ fontSize: 12, resize: 'vertical' }} />
                    </div>
                  </>
                )}

                <button className="btn btn-success btn-sm" disabled={uploading || (condition !== 'good' && damagePhotos.length === 0)}
                  onClick={() => markUnitWithCondition(unit.id)}>
                  <CheckIcon /> Submit — {CONDITIONS.find(c => c.id === condition)?.label}
                </button>
              </div>
            )}

            {/* Show damage info if received with issues */}
            {isReceived && arrData.condition !== 'good' && (
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--yellow)', width: '100%' }}>
                {arrData.damage_notes && <div style={{ fontStyle: 'italic' }}>"{arrData.damage_notes}"</div>}
                {arrData.damage_photos?.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                    {arrData.damage_photos.map((url, i) => (
                      <img key={i} src={url} alt="" style={{ width: 48, height: 36, borderRadius: 3, objectFit: 'cover' }} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default ContainerArrival;
